/**
 * TypeSelector Component
 *
 * Visual grid of requirement type options for FORGE Detailed Mode
 * Adapted from PSC Widget's TypeSelector with FORGE-specific types
 */

import React from 'react';
import type { RequirementType } from '../types/forge';
import styles from '../styles/forge.module.css';

// Icon components for each requirement type
const BugIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M8 2L6 6H18L16 2" />
    <path d="M12 6V22" />
    <path d="M6 12H2" />
    <path d="M22 12H18" />
    <path d="M6 18H2" />
    <path d="M22 18H18" />
    <ellipse cx="12" cy="14" rx="6" ry="8" />
  </svg>
);

const FeatureIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const EnhancementIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 2L12 6" />
    <path d="M12 18L12 22" />
    <path d="M4.93 4.93L7.76 7.76" />
    <path d="M16.24 16.24L19.07 19.07" />
    <path d="M2 12L6 12" />
    <path d="M18 12L22 12" />
    <path d="M4.93 19.07L7.76 16.24" />
    <path d="M16.24 7.76L19.07 4.93" />
  </svg>
);

const RefactorIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
    <line x1="12" y1="22.08" x2="12" y2="12" />
  </svg>
);

const DocsIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

const PerformanceIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
  </svg>
);

interface TypeOption {
  type: RequirementType;
  label: string;
  description: string;
  icon: React.ReactNode;
  colorClass: string;
}

const TYPE_OPTIONS: TypeOption[] = [
  {
    type: 'feature',
    label: 'New Feature',
    description: 'Add a new capability or functionality',
    icon: <FeatureIcon />,
    colorClass: 'forgeTypeFeature',
  },
  {
    type: 'enhancement',
    label: 'Enhancement',
    description: 'Improve an existing feature',
    icon: <EnhancementIcon />,
    colorClass: 'forgeTypeEnhancement',
  },
  {
    type: 'bug_fix',
    label: 'Bug Fix',
    description: 'Fix something that is not working correctly',
    icon: <BugIcon />,
    colorClass: 'forgeTypeBug',
  },
  {
    type: 'refactor',
    label: 'Refactor',
    description: 'Code cleanup or structural improvement',
    icon: <RefactorIcon />,
    colorClass: 'forgeTypeRefactor',
  },
  {
    type: 'documentation',
    label: 'Documentation',
    description: 'Update or add documentation',
    icon: <DocsIcon />,
    colorClass: 'forgeTypeDocs',
  },
];

interface TypeSelectorProps {
  selectedType: RequirementType | null;
  onSelect: (type: RequirementType) => void;
}

export const TypeSelector: React.FC<TypeSelectorProps> = ({ selectedType, onSelect }) => {
  return (
    <div className={styles.forgeTypeSelector}>
      <h3 className={styles.forgeTypeSelectorTitle}>What type of request is this?</h3>
      <div className={styles.forgeTypeSelectorGrid}>
        {TYPE_OPTIONS.map((option) => (
          <button
            key={option.type}
            onClick={() => onSelect(option.type)}
            className={`${styles.forgeTypeOption} ${styles[option.colorClass]} ${
              selectedType === option.type ? styles.forgeTypeOptionSelected : ''
            }`}
            data-testid={`type-${option.type}`}
            type="button"
          >
            <div className={styles.forgeTypeOptionIcon}>{option.icon}</div>
            <div className={styles.forgeTypeOptionContent}>
              <span className={styles.forgeTypeOptionLabel}>{option.label}</span>
              <span className={styles.forgeTypeOptionDescription}>{option.description}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default TypeSelector;
